// AES-256-GCM authenticated encryption for PII at rest.
//
// Rule 9 (no PII in logs, errors, analytics, test fixtures or AI prompts) +
// rule 11 (secrets never in the repo, scripts, configs or the database).
//
// Scheme (identical to tools/migration/src/staging/pii-encrypt.ts from PR A,
// promoted here as a shared primitive so ETL and future API/device code
// use the SAME ciphertext shape):
//   - 32-byte key.
//   - 12-byte random IV per value.
//   - 16-byte auth tag.
//   - Output: IV || CIPHERTEXT || TAG as Buffer, stored as Postgres `bytea`.
//
// Design notes:
//   - Per-value IV (not per-row) so an individual field can be decrypted
//     without touching the rest of the row.
//   - `null`/`undefined` is NOT encrypted — nullable PII columns are
//     allowed, and encrypting null would break Postgres nullable semantics.
//   - Values are first serialized to UTF-8 (strings), ISO-8601 (dates), or
//     a tagged form (numbers, booleans, Buffer). Decrypt reverses the tag.
//
// Key management lives in `./key-provider.ts`. In local staging/ETL the
// provider is a 32-byte key from `.env`. In production it's an AWS KMS
// envelope-encryption adapter — the ciphertext shape stays identical.

import { createCipheriv, createDecipheriv, randomBytes } from "node:crypto";

export const KEY_BYTES = 32;
export const IV_BYTES = 12;
export const TAG_BYTES = 16;

const TAG_STR = 0x01;
const TAG_INT = 0x02;
const TAG_BIG = 0x03;
const TAG_NUM = 0x04;
const TAG_BOOL = 0x05;
const TAG_DATE = 0x06;
const TAG_BUF = 0x07;

export class PiiKeyError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = "PiiKeyError";
  }
}

export class PiiCipherError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = "PiiCipherError";
  }
}

export function assertKey(key: Buffer): void {
  if (!Buffer.isBuffer(key) || key.length !== KEY_BYTES) {
    throw new PiiKeyError(
      `PII AES key must be ${KEY_BYTES} bytes; got ${
        Buffer.isBuffer(key) ? `${key.length} bytes` : typeof key
      }.`,
    );
  }
}

function tagValue(value: unknown): Buffer {
  if (typeof value === "string") {
    return Buffer.concat([Buffer.from([TAG_STR]), Buffer.from(value, "utf8")]);
  }
  if (typeof value === "number" && Number.isInteger(value)) {
    const buf = Buffer.alloc(9);
    buf[0] = TAG_INT;
    buf.writeBigInt64BE(BigInt(value), 1);
    return buf;
  }
  if (typeof value === "bigint") {
    const buf = Buffer.alloc(9);
    buf[0] = TAG_BIG;
    buf.writeBigInt64BE(value, 1);
    return buf;
  }
  if (typeof value === "number") {
    const buf = Buffer.alloc(9);
    buf[0] = TAG_NUM;
    buf.writeDoubleBE(value, 1);
    return buf;
  }
  if (typeof value === "boolean") {
    return Buffer.from([TAG_BOOL, value ? 1 : 0]);
  }
  if (value instanceof Date) {
    const buf = Buffer.alloc(9);
    buf[0] = TAG_DATE;
    buf.writeBigInt64BE(BigInt(value.getTime()), 1);
    return buf;
  }
  if (Buffer.isBuffer(value)) {
    return Buffer.concat([Buffer.from([TAG_BUF]), value]);
  }
  throw new PiiCipherError(
    `encrypt: unsupported value type ${typeof value}. Supported: string, number, bigint, boolean, Date, Buffer, null.`,
  );
}

function untagValue(buf: Buffer): unknown {
  if (buf.length === 0) throw new PiiCipherError("decrypt: empty payload");
  const tag = buf[0];
  const body = buf.subarray(1);
  switch (tag) {
    case TAG_STR:
      return body.toString("utf8");
    case TAG_INT: {
      if (body.length !== 8) throw new PiiCipherError("decrypt: bad int payload");
      // Encoder only writes TAG_INT for Number.isInteger values, which are
      // always within safe-integer range; a plain Number() round-trips exactly.
      return Number(body.readBigInt64BE(0));
    }
    case TAG_BIG: {
      if (body.length !== 8) throw new PiiCipherError("decrypt: bad bigint payload");
      return body.readBigInt64BE(0);
    }
    case TAG_NUM: {
      if (body.length !== 8) throw new PiiCipherError("decrypt: bad number payload");
      return body.readDoubleBE(0);
    }
    case TAG_BOOL: {
      if (body.length !== 1) throw new PiiCipherError("decrypt: bad boolean payload");
      return body[0] === 1;
    }
    case TAG_DATE: {
      if (body.length !== 8) throw new PiiCipherError("decrypt: bad date payload");
      return new Date(Number(body.readBigInt64BE(0)));
    }
    case TAG_BUF:
      return Buffer.from(body);
    default:
      throw new PiiCipherError(`decrypt: unknown tag 0x${tag?.toString(16)}`);
  }
}

export function encryptValue(value: unknown, key: Buffer): Buffer {
  assertKey(key);
  const iv = randomBytes(IV_BYTES);
  const cipher = createCipheriv("aes-256-gcm", key, iv);
  const payload = tagValue(value);
  const encrypted = Buffer.concat([cipher.update(payload), cipher.final()]);
  const tag = cipher.getAuthTag();
  return Buffer.concat([iv, encrypted, tag]);
}

export function decryptValue(ciphertext: Buffer, key: Buffer): unknown {
  assertKey(key);
  if (ciphertext.length < IV_BYTES + TAG_BYTES + 1) {
    throw new PiiCipherError("decrypt: ciphertext too short");
  }
  const iv = ciphertext.subarray(0, IV_BYTES);
  const tag = ciphertext.subarray(ciphertext.length - TAG_BYTES);
  const encrypted = ciphertext.subarray(IV_BYTES, ciphertext.length - TAG_BYTES);
  const decipher = createDecipheriv("aes-256-gcm", key, iv);
  decipher.setAuthTag(tag);
  const decrypted = Buffer.concat([decipher.update(encrypted), decipher.final()]);
  return untagValue(decrypted);
}

// Zero out a Buffer in place. Call this on decrypted plaintext buffers as
// soon as the plaintext has been consumed (e.g., re-encrypted + bidx'd).
// Prevents plaintext from lingering in memory any longer than necessary.
export function zeroBuffer(buf: Buffer | null | undefined): void {
  if (buf && Buffer.isBuffer(buf) && buf.length > 0) {
    buf.fill(0);
  }
}
